/**
 * Shared SVG icon components used across dashboard pages.
 * Extracted to eliminate duplicated icon definitions.
 */

/** Document icon -- used for review/info/recommendations contexts. */
export function DocumentIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
      <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" />
      <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" />
      <path d="M7 13h6M7 17h4" />
    </svg>
  );
}

/** Edit/pencil icon -- used for "Take notes", "Doctor Note", and similar edit actions. */
export function EditIcon({ className }: { className?: string } = {}) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M11 2H9C4 2 2 4 2 9v6c0 5 2 7 7 7h6c5 0 7-2 7-7v-2" />
      <path d="M15.35 3.32l-6.64 6.64c-.25.25-.5.74-.55 1.09l-.37 2.58c-.13.93.57 1.63 1.5 1.5l2.58-.37c.34-.05.84-.3 1.09-.55l6.64-6.64c1.15-1.15 1.69-2.48 0-4.17-1.69-1.69-3.02-1.23-4.17-.08Z" />
    </svg>
  );
}

/** Eye/view icon for read-only actions. */
export function ViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.58 12c0 1.98-1.6 3.58-3.58 3.58S8.42 13.98 8.42 12s1.6-3.58 3.58-3.58 3.58 1.6 3.58 3.58Z" />
      <path d="M12 20.27c3.53 0 6.82-2.08 9.11-5.68.9-1.41.9-3.78 0-5.19C18.82 5.81 15.53 3.73 12 3.73S5.18 5.81 2.89 9.41c-.9 1.41-.9 3.78 0 5.19 2.29 3.6 5.58 5.67 9.11 5.67Z" />
    </svg>
  );
}

/** Bar chart icon for hormone results. */
export function HormoneIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
      <path d="M2 22h20" />
      <path d="M9.75 4v18" />
      <path d="M14.25 10v12" />
      <path d="M5.25 10v12" />
      <path d="M18.75 7v15" />
    </svg>
  );
}

/** Circle-info icon for clinical scenarios. */
export function ScenarioIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
      <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" />
      <path d="M12 8v5" />
      <path d="M11.995 16h.01" />
    </svg>
  );
}

/** Calendar icon for cycle context. */
export function CycleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
      <path d="M8 2v3M16 2v3" />
      <path d="M3.5 9.09h17" />
      <path d="M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z" />
    </svg>
  );
}

/** User/person icon for client info. */
export function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
      <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM20.59 22c0-3.87-3.85-7-8.59-7s-8.59 3.13-8.59 7" />
    </svg>
  );
}

/** Note/edit icon for doctor notes section -- same shape as EditIcon with section styling. */
export function NoteIcon() {
  return <EditIcon className="text-text-primary" />;
}

/** Eye icon (24x24) used for the Preview button in the consultation workspace. */
export function EyeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.58 12c0 1.98-1.6 3.58-3.58 3.58S8.42 13.98 8.42 12s1.6-3.58 3.58-3.58 3.58 1.6 3.58 3.58z" />
      <path d="M12 20.27c3.53 0 6.82-2.08 9.11-5.68.9-1.41.9-3.78 0-5.19C18.82 5.81 15.53 3.73 12 3.73S5.18 5.81 2.89 9.41c-.9 1.41-.9 3.78 0 5.18 2.29 3.6 5.58 5.68 9.11 5.68z" />
    </svg>
  );
}

/** Calendar-plan icon for "Plan / next steps" section. */
export function SymptomIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      <circle cx="18" cy="18" r="4" fill="var(--color-white)" stroke="currentColor" />
      <path d="M16.5 18h3M18 16.5v3" />
    </svg>
  );
}

export function PlanIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary">
      <path d="M8 22h8c4.42 0 5.26-1.79 5.47-3.97L22.17 8c.27-2.75-.63-5-4.17-5H6c-3.54 0-4.44 2.25-4.17 5l.7 10.03C2.74 20.21 3.58 22 8 22Z" />
      <path d="M8 2v3M16 2v3M2.5 9h19" />
    </svg>
  );
}
